import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useShallowStableRecord } from '../../../hooks/useShallowStableRecord';

import { hydrateEconomicWorkforceProfile } from '../../../services/economicContributionClient';
import type {
  EconomicProfileSnapshot,
  EconomicWorkforceProfileRequest,
  EconomicWorkforceProfileResponse,
} from '../../../types/economicContribution';
import type { SiteEntity } from '../../../types/site';
import type { RefdataFipsResult } from '../../../services/refdata/fipsLookup';

/** Kept aligned with the server cache policy for annual federal datasets. */
export const ECONOMIC_WORKFORCE_PROFILE_STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1_000;
const MAX_CLOCK_SKEW_MS = 5 * 60 * 1_000;

type HydrateProfile = (
  request: EconomicWorkforceProfileRequest,
  options?: { signal?: AbortSignal },
) => Promise<EconomicWorkforceProfileResponse>;

export interface UseEconomicWorkforceProfileOptions {
  site: SiteEntity;
  /** Enable only while the Findings surface is actually open. */
  enabled: boolean;
  staleAfterMs?: number;
  now?: () => number;
  /** Test seam; production always uses the authenticated client. */
  hydrateProfile?: HydrateProfile;
}

export interface EconomicWorkforceProfileController {
  profile: EconomicProfileSnapshot | null;
  loading: boolean;
  error: string | null;
  stale: boolean;
  countyMismatch: boolean;
  /** True until this exact site/owner/coordinate identity has a server response. */
  locationCheckPending: boolean;
  /** True only after the server has resolved this exact coordinate identity. */
  locationVerified: boolean;
  /**
   * A fresh saved profile whose county agrees with the server-authored economic
   * screening location. It may remain visible during a failed refresh, but is
   * never reused as an exact-coordinate FIPS shortcut.
   */
  savedProfileUsableForDisplay: boolean;
  refresh: () => Promise<void>;
}

export function shouldWithholdEconomicWorkforceProfile(
  controller: Pick<
    EconomicWorkforceProfileController,
    'locationCheckPending' | 'locationVerified' | 'savedProfileUsableForDisplay' | 'countyMismatch' | 'error'
  >,
): boolean {
  if (controller.countyMismatch) return true;
  if (controller.locationVerified) return false;
  // Do not flash a prior-county profile while the exact coordinate check is
  // genuinely in flight. The saved fallback is used only after that check
  // fails, with an explicit refresh warning alongside it.
  if (controller.locationCheckPending) return true;
  if (controller.error != null) return !controller.savedProfileUsableForDisplay;
  return false;
}

/** Reuse only a profile confirmed for this exact site/coordinate request. */
export function verifiedWorkforceProfileFips(
  controller: Pick<
    EconomicWorkforceProfileController,
    'profile' | 'locationVerified' | 'locationCheckPending' | 'savedProfileUsableForDisplay' | 'countyMismatch' | 'error'
  >,
): RefdataFipsResult | undefined {
  const { profile } = controller;
  if (!profile || !controller.locationVerified || shouldWithholdEconomicWorkforceProfile(controller)) return undefined;
  if (!/^\d{5}$/.test(profile.countyFips) || !/^\d{2}$/.test(profile.stateFips)) return undefined;
  if (!profile.countyName.trim() || !/^[A-Z]{2}$/.test(profile.stateCode)) return undefined;
  return {
    countyFips: profile.countyFips,
    stateFips: profile.stateFips,
    countyName: profile.countyName,
    stateCode: profile.stateCode,
  };
}

function validCoordinates(site: SiteEntity): boolean {
  const { lat, lng } = site.location;
  return Number.isFinite(lat)
    && Number.isFinite(lng)
    && lat >= -90
    && lat <= 90
    && lng >= -180
    && lng <= 180;
}

export function isEconomicWorkforceProfileStale(
  profile: EconomicProfileSnapshot,
  nowMs = Date.now(),
  staleAfterMs = ECONOMIC_WORKFORCE_PROFILE_STALE_AFTER_MS,
): boolean {
  const generatedAtMs = Date.parse(profile.generatedAt);
  if (!Number.isFinite(generatedAtMs)) return true;
  const ageMs = nowMs - generatedAtMs;
  return ageMs < -MAX_CLOCK_SKEW_MS || ageMs > staleAfterMs;
}

function messageOf(error: unknown): string {
  return error instanceof Error && error.message.trim()
    ? error.message
    : 'Public workforce context could not be loaded.';
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';
}

/**
 * Performs one canonical-location workforce check per site/owner/coordinate
 * identity when Findings opens. The server resolves FIPS and cheaply returns
 * `unchanged` for a current same-county profile, so this path never needs MW,
 * jobs-model inputs, or report hydration.
 */
export function useEconomicWorkforceProfile({
  site,
  enabled,
  staleAfterMs = ECONOMIC_WORKFORCE_PROFILE_STALE_AFTER_MS,
  now = Date.now,
  hydrateProfile = hydrateEconomicWorkforceProfile,
}: UseEconomicWorkforceProfileOptions): EconomicWorkforceProfileController {
  const identity = useMemo(() => [
    site.id,
    site._ownerUid ?? '',
    site.location.lat,
    site.location.lng,
  ].join(':'), [site._ownerUid, site.id, site.location.lat, site.location.lng]);
  const [local, setLocal] = useState<{ identity: string; profile: EconomicProfileSnapshot } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestedIdentityRef = useRef<string | null>(null);
  const inFlightRef = useRef<{ identity: string; promise: Promise<void> } | null>(null);
  const activeIdentityRef = useRef(identity);
  const mountedRef = useRef(true);
  const controllersRef = useRef(new Set<AbortController>());

  activeIdentityRef.current = identity;
  const localProfile = local?.identity === identity ? local.profile : null;
  // A successful server response is bound to this exact site/owner/coordinate
  // identity and therefore outranks an older saved snapshot regardless of its
  // timestamp. The saved economic screen may still describe prior coordinates.
  const profile = localProfile ?? site.economicProfileSnapshot ?? null;
  const expectedCountyFips = site.economicContributionSnapshot?.location.countyFips;
  const expectedStateFips = site.economicContributionSnapshot?.location.stateFips;
  const countyMismatch = Boolean(
    localProfile == null
    && profile
    && typeof expectedCountyFips === 'string'
    && /^\d{5}$/.test(expectedCountyFips)
    && profile.countyFips !== expectedCountyFips,
  );
  const stale = profile ? isEconomicWorkforceProfileStale(profile, now(), staleAfterMs) : false;
  const savedProfileUsableForDisplay = localProfile == null
    && profile != null
    && !stale
    && site.economicContributionSnapshot?.siteId === site.id
    && typeof expectedCountyFips === 'string'
    && /^\d{5}$/.test(expectedCountyFips)
    && typeof expectedStateFips === 'string'
    && /^\d{2}$/.test(expectedStateFips)
    && profile.countyFips === expectedCountyFips
    && profile.stateFips === expectedStateFips;
  const eligible = enabled && validCoordinates(site);
  const locationCheckPending = eligible
    && !site.draftOnly
    && localProfile == null
    && (loading || requestedIdentityRef.current !== identity);

  useEffect(() => {
    // React StrictMode replays this lifecycle; re-arm without changing the
    // request identity, which keeps the server-side operation idempotent.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // StrictMode immediately replays setup after its development-only
      // cleanup. Defer cancellation by one microtask so that replay retains
      // the single request; a real unmount remains inactive and aborts it.
      queueMicrotask(() => {
        if (mountedRef.current) return;
        for (const controller of controllersRef.current) controller.abort();
        controllersRef.current.clear();
      });
    };
  }, []);

  useEffect(() => {
    setLoading(false);
    setError(null);
    inFlightRef.current = null;
  }, [identity]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!enabled || !validCoordinates(site)) return;
    const existing = inFlightRef.current;
    if (existing?.identity === identity) return existing.promise;
    const controller = new AbortController();
    controllersRef.current.add(controller);
    setLoading(true);
    setError(null);
    const requestIdentity = identity;
    const promise = (async () => {
      try {
        const response = await hydrateProfile({
          siteId: site.id,
          ...(site._ownerUid ? { ownerUid: site._ownerUid } : {}),
        }, { signal: controller.signal });
        if (
          !mountedRef.current
          || controller.signal.aborted
          || activeIdentityRef.current !== requestIdentity
        ) return;
        setLocal({ identity: requestIdentity, profile: response.profile });
      } catch (cause) {
        if (
          !controller.signal.aborted
          && !isAbort(cause)
          && mountedRef.current
          && activeIdentityRef.current === requestIdentity
        ) setError(messageOf(cause));
      } finally {
        controllersRef.current.delete(controller);
        if (mountedRef.current && activeIdentityRef.current === requestIdentity) setLoading(false);
        if (inFlightRef.current?.identity === requestIdentity) inFlightRef.current = null;
      }
    })();
    inFlightRef.current = { identity: requestIdentity, promise };
    return promise;
  }, [enabled, hydrateProfile, identity, site]);

  useEffect(() => {
    if (site.draftOnly || !eligible || requestedIdentityRef.current === identity) return;
    requestedIdentityRef.current = identity;
    void refresh();
  }, [eligible, identity, refresh, site.draftOnly]);

  // Value-stable controller: the host hands this object to a memoised child,
  // so a fresh object per render with unchanged members must not count as a
  // changed prop.
  return useShallowStableRecord({
    profile,
    loading,
    error,
    stale,
    countyMismatch,
    locationCheckPending,
    locationVerified: localProfile != null,
    savedProfileUsableForDisplay,
    refresh,
  });
}
